import React, { useCallback, useState } from 'react';
import TimePicker24Hrs from './TimePicker24Hrs';
import { DatePickerWithInput } from './DatePickerWithInput';
import { toZonedTime } from 'date-fns-tz';
import { shouldDisableDate } from './shouldDisableDate';

// Extract just the date part from a datetime format pattern
const extractDatePattern = (dateTimeFormat: string): string => {
  // Remove time components for the date input
  return dateTimeFormat
    .replace(/\s*HH.*$/, '') // Remove HH:mm:ss and everything after
    .replace(/\s*hh.*$/, '') // Remove hh:mm:ss and everything after
    .trim();
};

interface DateTimeValue {
  date: Date;
  dateString: string;
  timeString: string;
}

interface DatetimePickerWithInputProps {
  value?: DateTimeValue;
  onChange?: (value: DateTimeValue) => void;
  dateTimeFormat?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  dateLimit?: 'past' | 'future' | 'all';
  startDatetimeThreshold?: Date;
  endDatetimeThreshold?: Date;
  timeZone: string;
}

export function DatetimePickerWithInput({
  value,
  onChange,
  dateTimeFormat = 'yyyy-MM-dd HH:mm:ss',
  placeholder,
  className = '',
  disabled = false,
  dateLimit = 'all',
  startDatetimeThreshold,
  endDatetimeThreshold,
  timeZone,
}: DatetimePickerWithInputProps) {
  const dateOnlyPattern = extractDatePattern(dateTimeFormat);

  const disableDate = useCallback(
    (zonedDateToCheck: Date) =>
      shouldDisableDate(zonedDateToCheck, {
        timeZone,
        startDatetimeThreshold,
        endDatetimeThreshold,
        dateLimit,
      }),
    [timeZone, startDatetimeThreshold, endDatetimeThreshold, dateLimit],
  );

  const validateDateTime = useCallback(
    (date: Date): boolean => {
      const today = new Date();
      const zonedToday = toZonedTime(today, timeZone);

      if (startDatetimeThreshold && date < startDatetimeThreshold) {
        return false;
      }
      if (endDatetimeThreshold && date > endDatetimeThreshold) {
        return false;
      }

      switch (dateLimit) {
        case 'past':
          return date <= today;
        case 'future':
          return date >= today;
        case 'all':
        default:
          return true;
      }
    },
    [timeZone, startDatetimeThreshold, endDatetimeThreshold, dateLimit],
  );

  const [timeValue, setTimeValue] = useState<string>(() => {
    if (!value?.date) return '10:30:00';
    const hours = value.date.getHours().toString().padStart(2, '0');
    const minutes = value.date.getMinutes().toString().padStart(2, '0');
    const seconds = value.date.getSeconds().toString().padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  });

  const combineDateTime = (date: Date, time: string): Date => {
    const [hours, minutes, seconds = '00'] = time.split(':');
    const combined = new Date(date);
    combined.setHours(parseInt(hours), parseInt(minutes), parseInt(seconds));
    return combined;
  };

  const formatDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');

    if (dateOnlyPattern.includes('yyyy-MM-dd')) {
      return `${year}-${month}-${day}`;
    } else if (dateOnlyPattern.includes('MM/dd/yyyy')) {
      return `${month}/${day}/${year}`;
    } else if (dateOnlyPattern.includes('dd/MM/yyyy')) {
      return `${day}/${month}/${year}`;
    }
    return `${year}-${month}-${day}`;
  };

  const handleDateChange = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      const combined = combineDateTime(selectedDate, timeValue);
      const dateString = formatDateString(selectedDate);

      // Always allow the change - let form validation handle it
      if (!value?.date || combined.getTime() !== value.date.getTime()) {
        onChange?.({
          date: combined,
          dateString: dateString,
          timeString: timeValue,
        });
      }
    }
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = e.target.value;
    setTimeValue(newTime);

    if (value?.date) {
      const combined = combineDateTime(value.date, newTime);

      // Validate the combined datetime
      if (!validateDateTime(combined)) {
        return; // Silently reject invalid times
      }

      if (combined.getTime() !== value.date.getTime()) {
        onChange?.({
          date: combined,
          dateString: value.dateString,
          timeString: newTime,
        });
      }
    }
  };

  return (
    <div className={`flex gap-4 ${className}`}>
      <div className="flex flex-col gap-3 flex-1">
        <DatePickerWithInput
          id="datetime-date-input"
          value={value?.date}
          onChange={handleDateChange}
          dateFormat={dateOnlyPattern}
          placeholder={placeholder}
          disabled={disabled}
          disableDate={disableDate}
        />
      </div>

      <div className="flex flex-col gap-3">
        <TimePicker24Hrs
          id="datetime-time-input"
          value={timeValue}
          onChange={handleTimeChange}
          className="bg-background w-36"
        />
      </div>
    </div>
  );
}
